import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { User, Menu, Home, Settings, HelpCircle, ExternalLink, Globe, ChevronDown, Pin, AlertTriangle, CircleDollarSign, Link } from 'lucide-react';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useUser } from '../hooks/useUser';
import { useLanguage } from '../hooks/useLanguage';

export const FAQPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentLanguage, switchLanguage } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');


  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);

  const languageItems = [
    {
      id: 'eng',
      label: 'English',
      onClick: () => switchLanguage('ENG')
    },
    {
      id: 'rus',
      label: 'Русский',
      onClick: () => switchLanguage('RUS')
    }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  // Вопросы и ответы для FAQ
  const faqItems: { question: string; answer: string }[] = [
    { question: t('faq_q1'), answer: t('faq_a1') },
    { question: t('faq_q2'), answer: t('faq_a2') },
    { question: t('faq_q3'), answer: t('faq_a3') },
    { question: t('faq_q4'), answer: t('faq_a4') },
    { question: t('faq_q5'), answer: t('faq_a5') },
    { question: t('faq_q6'), answer: t('faq_a6') },
    { question: t('faq_q7'), answer: t('faq_a7') },
    { question: t('faq_q8'), answer: t('faq_a8') },
    { question: t('faq_q9'), answer: t('faq_a9') },
    { question: t('faq_q10'), answer: t('faq_a10') },
    { question: t('faq_q11'), answer: t('faq_a11') },
    { question: t('faq_q12'), answer: t('faq_a12') },
    { question: t('faq_q13'), answer: t('faq_a13') },
    { question: t('faq_q14'), answer: t('faq_a14') },
    { question: t('faq_q15'), answer: t('faq_a15') },
    { question: t('faq_q16'), answer: t('faq_a16') },
    { question: t('faq_q17'), answer: t('faq_a17') },
    { question: t('faq_q18'), answer: t('faq_a18') },
    { question: t('faq_q19'), answer: t('faq_a19') },
    { question: t('faq_q20'), answer: t('faq_a20') },
    { question: t('faq_q21'), answer: t('faq_a21') },
    { question: t('faq_q22'), answer: t('faq_a22') },
    { question: t('faq_q23'), answer: t('faq_a23') },
    { question: t('faq_q24'), answer: t('faq_a24') },
  ];

  // Функция для рендера иконок и переносов строк в тексте ответа
  function renderAnswer(answer: string) {
    return answer.split(/(<br\/>|<PinIcon \/>|<AlertTriangle \/>|<img[^>]*\/>)/g).map((part, idx) => {
      if (part === '<br/>') return <br key={idx} />;
      if (part === '<PinIcon />') return <Pin key={idx} className="inline w-5 h-5 text-primary align-text-bottom mx-1" />;
      if (part === '<AlertTriangle />') return <AlertTriangle key={idx} className="inline w-5 h-5 text-yellow-500 align-text-bottom mx-1" />;
      if (part.startsWith('<img')) {
        // Извлекаем атрибуты из тега img
        const srcMatch = part.match(/src=['"]([^'"]+)['"]/);
        const altMatch = part.match(/alt=['"]([^'"]+)['"]/);
        const classNameMatch = part.match(/className=['"]([^'"]+)['"]/);
        const styleMatch = part.match(/style=['"]([^'"]+)['"]/);
        
        const src = srcMatch ? srcMatch[1] : '';
        const alt = altMatch ? altMatch[1] : '';
        const className = classNameMatch ? classNameMatch[1] : 'inline w-5 h-5 align-text-bottom mx-1';
        
        // Парсим стили
        let style: any = {};
        if (styleMatch) {
          const styleString = styleMatch[1];
          styleString.split(';').forEach(rule => {
            const [property, value] = rule.split(':').map(s => s.trim());
            if (property && value) {
              // Конвертируем CSS свойство в camelCase для React
              const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              style[camelProperty] = value;
            }
          });
        }
        
        return <img key={idx} src={src} alt={alt} className={className} style={style} />;
      }
      return part;
    });
  }

  return (
    <div className="min-h-screen bg-background" 
         style={{ paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + ${safeAreaTop})` : '5rem' }}>
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Dropdown
              id="account"
              trigger={<User className="w-5 h-5 text-iconPrimary" />}
              items={accountItems}
              align="right"
            />
            <Dropdown
              id="language"
              trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>}
              items={languageItems}
              align="right"
            />
            <Dropdown
              id="pages"
              trigger={<Menu className="w-5 h-5 text-iconPrimary" />}
              items={pagesItems}
              align="right"
            />
          </div>
        </div>
      </header>
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 text-center font-sans flex items-center justify-center gap-2">
          <HelpCircle className="w-7 h-7 text-primary" />
          {t('faq')}
        </h2>
        <div className="text-lg md:text-2xl text-textSubtle text-center mb-8 font-sans">
          {t('faq_intro')}
        </div>
        <div className="flex flex-col gap-4 w-full">
          {faqItems.map((item, idx) => (
            <div key={idx} className="w-full">
              <button
                className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openIndex === idx ? 'shadow-lg button-glow' : ''}`}
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                aria-expanded={openIndex === idx}
              >
                <span className="text-left">{item.question}</span>
                <span className={`ml-2 transition-transform ${openIndex === idx ? 'rotate-180' : ''}`}>
                  <ChevronDown className="w-5 h-5" />
                </span>
              </button>
              {openIndex === idx && (
                <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                  {renderAnswer(item.answer)}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}; 